import type { ConversationId } from "@redotech/effect-api-shared/ids/definitions";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { useSingleConversationData } from "../hooks/use-conversation-data";

interface ConversationViewProps {
  conversationId: ConversationId;
}

/**
 * Conversation view component that displays conversation data
 * using TanStack DB live queries
 */
export const ConversationView = memo(function ConversationView({
  conversationId,
}: ConversationViewProps) {
  const { conversation, threads, messages, isLoading } =
    useSingleConversationData(conversationId);

  if (isLoading) {
    return (
      <Flex align="center" justify="center" p="xl">
        <RedoSpinner />
      </Flex>
    );
  }

  if (!conversation) {
    return (
      <Flex align="center" justify="center" p="xl">
        <Text>Conversation not found</Text>
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="lg" p="lg">
      <Flex dir="column" gap="sm">
        <Text fontSize="lg" fontWeight="semibold">
          Conversation: {conversation.id}
        </Text>
        <Text fontSize="sm" textColor="secondary">
          Status: {conversation.status}
        </Text>
        {conversation.priority && (
          <Text fontSize="sm" textColor="secondary">
            Priority: {conversation.priority}
          </Text>
        )}
      </Flex>

      <Flex dir="column" gap="sm">
        <Text fontSize="md" fontWeight="semibold">
          Threads ({threads.length})
        </Text>
        {threads.map((thread) => (
          <Flex
            dir="column"
            gap="xs"
            key={thread.id}
            p="sm"
            style={{ border: "1px solid var(--redo-colors-border-primary)" }}
          >
            <Text fontSize="sm">Thread ID: {thread.id}</Text>
            <Text fontSize="xs" textColor="secondary">
              Integration: {thread.integrationType}
            </Text>
          </Flex>
        ))}
      </Flex>

      <Flex dir="column" gap="sm">
        <Text fontSize="md" fontWeight="semibold">
          Messages ({messages.length})
        </Text>
        {messages.map((message) => (
          <Flex
            dir="column"
            gap="xs"
            key={message.id}
            p="sm"
            style={{ border: "1px solid var(--redo-colors-border-primary)" }}
          >
            <Flex justify="space-between">
              <Text fontSize="sm" fontWeight="medium">
                {message.origin === "external" ? "Customer" : "Agent"}
              </Text>
              <Text fontSize="xs" textColor="secondary">
                {new Date(message.sentAt).toLocaleString()}
              </Text>
            </Flex>
            <Text fontSize="sm">{message.content || "(No content)"}</Text>
            <Text fontSize="xs" textColor="secondary">
              Channel: {message.channel}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
});
